// =============================================================================
// QuestionnaireSettings — Template library sourced from Clinio Forms
// =============================================================================
// Linear: CLINIO-217 (FORMS-6)
//
// Replaces the legacy v2 questionnaire_templates CRUD page with a read-only
// view of the Forms-hosted template library. Clinics can:
//   - Browse platform PROMs (read-only, "Platform-skabelon" badge)
//   - Browse their own custom templates
//   - Click "Rediger i Clinio Forms" to edit custom templates in Forms admin
//   - Preview a template in a modal before using it
//
// Template editing lives entirely in Clinio Forms admin UI.
// This page does NOT host a questionnaire editor (v2 spec, FORMS-1 ADD §12).
// =============================================================================

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  ExternalLink,
  Eye,
  Filter,
  Loader2,
  Link2,
  X,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { Button } from '@/components/ui/Button';
import {
  useFormsTemplateList,
  useFormsTemplateDetail,
  type FormsTemplateSummary,
} from '@/hooks/useFormsTemplates';
import {
  useFormsTenantStatus,
  useProvisionFormsTenant,
} from '@/hooks/useClinioForms';
import { formsAdminUrl } from '@/lib/formsUrls';
import { IntegrationStatusBanner } from '@/components/IntegrationStatusBanner';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TemplateBadge({ variant, label }: { variant: 'platform' | 'license' | 'custom'; label: string }) {
  const styles = {
    platform: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    license:  'bg-amber-100  text-amber-700  dark:bg-amber-900/30  dark:text-amber-300',
    custom:   'bg-gray-100   text-gray-600   dark:bg-gray-700      dark:text-gray-300',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[variant]}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ConnectBanner — shown when clinic is not yet provisioned in Clinio Forms
// ---------------------------------------------------------------------------

function ConnectBanner({
  clinicId,
  onConnected,
}: {
  clinicId: string;
  onConnected: () => void;
}) {
  const { t } = useTranslation();
  const provision = useProvisionFormsTenant(clinicId);

  const handleConnect = async () => {
    try {
      await provision.mutateAsync();
      onConnected();
    } catch (_e) {
      // error surfaced via provision.error below
    }
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-6 dark:border-indigo-800/50 dark:bg-indigo-900/20">
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-indigo-100 p-2.5 dark:bg-indigo-900/40">
          <Link2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
            {t('questionnaires.connect_title')}
          </h3>
          <p className="mt-1 text-sm text-indigo-700 dark:text-indigo-300">
            {t('questionnaires.connect_desc')}
          </p>
          {provision.error && (
            <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">
              {(provision.error as Error).message}
            </p>
          )}
        </div>
        <Button
          onClick={handleConnect}
          disabled={provision.isPending}
          className="shrink-0"
        >
          {provision.isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('questionnaires.connect_loading')}
            </span>
          ) : (
            t('questionnaires.connect_btn')
          )}
        </Button>
      </div>
    </div>
  );
}

type PreviewModalProps = {
  clinicId: string;
  template: FormsTemplateSummary;
  locale: 'da-DK' | 'en-DK';
  onClose: () => void;
};

function PreviewModal({ clinicId, template, locale, onClose }: PreviewModalProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useFormsTemplateDetail(clinicId, template.id);
  const displayName = locale === 'da-DK' ? template.display_name_da : template.display_name_en;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-16 max-w-lg rounded-xl bg-white shadow-xl dark:bg-card-dark">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{displayName}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : data ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {template.is_platform && (
                  <TemplateBadge
                    variant="platform"
                    label={locale === 'da-DK'
                      ? t('questionnaires.platform_template_badge')
                      : t('questionnaires.platform_template_badge')}
                  />
                )}
                {template.seed_status === 'content_pending_license' && (
                  <TemplateBadge
                    variant="license"
                    label={locale === 'da-DK'
                      ? t('questionnaires.license_pending_badge')
                      : t('questionnaires.license_pending_badge_en')}
                  />
                )}
              </div>

              {data.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">{data.description}</p>
              )}

              <dl className="grid grid-cols-2 gap-3 text-sm">
                {data.estimated_minutes != null && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">{t('questionnaires.estimated_minutes')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {data.estimated_minutes} min
                    </dd>
                  </>
                )}
                {data.category && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">{t('questionnaires.category')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white capitalize">
                      {data.category.replace(/_/g, ' ')}
                    </dd>
                  </>
                )}
                <dt className="text-gray-500 dark:text-gray-400">{t('questionnaires.version')}</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{data.version}</dd>
              </dl>

              {data.seed_status === 'content_pending_license' && (
                <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {t('questionnaires.license_pending_notice')}
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {t('questionnaires.preview_unavailable')}
            </p>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

type TemplateCardProps = {
  template: FormsTemplateSummary;
  locale: 'da-DK' | 'en-DK';
  clinicId: string;
  formsTenantId?: string | null;
  onPreview: (t: FormsTemplateSummary) => void;
};

function TemplateCard({ template, locale, formsTenantId, onPreview }: TemplateCardProps) {
  const { t } = useTranslation();
  const displayName = locale === 'da-DK' ? template.display_name_da : template.display_name_en;
  const isPendingLicense = template.seed_status === 'content_pending_license';

  const editUrl = formsAdminUrl({
    tenantId: formsTenantId,
    path: `/questionnaires/${template.id}/edit`,
  });

  return (
    <div className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-gray-900 dark:text-white">{displayName}</p>

          {template.is_platform && (
            <TemplateBadge
              variant="platform"
              label={locale === 'da-DK'
                ? t('questionnaires.platform_template_badge')
                : t('questionnaires.platform_template_badge_en')}
            />
          )}

          {isPendingLicense && (
            <TemplateBadge
              variant="license"
              label={locale === 'da-DK'
                ? t('questionnaires.license_pending_badge')
                : t('questionnaires.license_pending_badge_en')}
            />
          )}
        </div>

        {template.description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{template.description}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {template.estimated_minutes != null && (
            <span className="flex items-center gap-1">
              <ClipboardList className="h-3.5 w-3.5" />
              {template.estimated_minutes} min
            </span>
          )}
          {template.category && (
            <span className="capitalize">{template.category.replace(/_/g, ' ')}</span>
          )}
          <span className="font-mono">{template.version}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          title={t('questionnaires.preview')}
          onClick={() => onPreview(template)}
          className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <Eye className="h-4 w-4" />
        </button>

        {!template.is_platform && (
          <a
            href={editUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('questionnaires.edit_in_forms')}
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QuestionnaireSettings() {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();

  const uiLocale: 'da-DK' | 'en-DK' = i18n.language?.startsWith('da') ? 'da-DK' : 'en-DK';

  const [filterLocale, setFilterLocale] = useState<'' | 'da-DK' | 'en-DK'>('');
  const [filterCategory, setFilterCategory] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<FormsTemplateSummary | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const tenantStatus = useFormsTenantStatus(activeClinicId);
  const isProvisioned = tenantStatus.data?.status === 'active';
  const formsTenantId = tenantStatus.data?.forms_tenant_id ?? null;
  const openFormsHref = formsAdminUrl({ tenantId: formsTenantId });

  const templatesQuery = useFormsTemplateList(
    activeClinicId,
    filterLocale || undefined,
  );

  const library = useMemo(() => {
    if (templatesQuery.data) return templatesQuery.data;
    return null;
  }, [templatesQuery.data]);

  const allCategories = useMemo(() => {
    if (!library) return [];
    const cats = new Set<string>();
    [...library.platform, ...library.clinic].forEach((t) => {
      if (t.category) cats.add(t.category);
    });
    return Array.from(cats).sort();
  }, [library]);

  function applyFilter(items: FormsTemplateSummary[]) {
    if (!filterCategory) return items;
    return items.filter((t) => t.category === filterCategory);
  }

  const platformTemplates = library ? applyFilter(library.platform) : [];
  const clinicTemplates   = library ? applyFilter(library.clinic)   : [];

  const isEmpty = !templatesQuery.isLoading && platformTemplates.length === 0 && clinicTemplates.length === 0;

  return (
    <div>
      <IntegrationsBackLink />

      {/* Clinio Forms service availability banner (SHARED-5) */}
      <IntegrationStatusBanner integration="forms" className="mb-4" />

      {/* ── Connect banner (shown when clinic not yet provisioned) ── */}
      {!tenantStatus.isLoading && !isProvisioned && activeClinicId && (
        <ConnectBanner
          clinicId={activeClinicId}
          onConnected={() => tenantStatus.refetch()}
        />
      )}

      {/* ── Header ── */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.integrations.cards.forms_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('questionnaires.forms_subtitle')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isProvisioned && (
            <a
              href={openFormsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <ExternalLink className="h-4 w-4" />
              {t('questionnaires.open_forms_admin', 'Åbn Clinio Forms')}
            </a>
          )}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Filter className="h-4 w-4" />
            {t('questionnaires.filter')}
          </button>
        </div>
      </div>

      {/* ── Filter panel ── */}
      {showFilters && (
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('questionnaires.filter_locale')}
            </label>
            <select
              value={filterLocale}
              onChange={(e) => setFilterLocale(e.target.value as '' | 'da-DK' | 'en-DK')}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            >
              <option value="">{t('questionnaires.filter_all')}</option>
              <option value="da-DK">Dansk (da-DK)</option>
              <option value="en-DK">English (en-DK)</option>
            </select>
          </div>

          {allCategories.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('questionnaires.category')}
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                <option value="">{t('questionnaires.filter_all')}</option>
                {allCategories.map((cat) => (
                  <option key={cat} value={cat} className="capitalize">
                    {cat.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(filterLocale || filterCategory) && (
            <button
              type="button"
              onClick={() => { setFilterLocale(''); setFilterCategory(''); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
              {t('questionnaires.clear_filters')}
            </button>
          )}
        </div>
      )}

      {/* ── Loading ── */}
      {templatesQuery.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* ── Error ── */}
      {templatesQuery.isError && !templatesQuery.isLoading && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
          {t('questionnaires.load_error')}
        </div>
      )}

      {/* ── Empty ── */}
      {isEmpty && !templatesQuery.isLoading && !templatesQuery.isError && (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <ClipboardList className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {isProvisioned
              ? t('questionnaires.no_templates_connected')
              : t('questionnaires.no_templates_forms')}
          </p>
          {isProvisioned && (
            <a
              href={openFormsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <ExternalLink className="h-4 w-4" />
              {t('questionnaires.create_in_forms')}
            </a>
          )}
        </div>
      )}

      {/* ── Platform PROM library ── */}
      {!templatesQuery.isLoading && platformTemplates.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('questionnaires.platform_section')}
          </h3>
          <div className="space-y-3">
            {platformTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                locale={uiLocale}
                clinicId={activeClinicId ?? ''}
                formsTenantId={formsTenantId}
                onPreview={setPreviewTemplate}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Clinic custom templates ── */}
      {!templatesQuery.isLoading && clinicTemplates.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('questionnaires.custom_section')}
          </h3>
          <div className="space-y-3">
            {clinicTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                locale={uiLocale}
                clinicId={activeClinicId ?? ''}
                formsTenantId={formsTenantId}
                onPreview={setPreviewTemplate}
              />
            ))}
          </div>
        </section>
      )}


      {/* ── Preview modal ── */}
      {previewTemplate && activeClinicId && (
        <PreviewModal
          clinicId={activeClinicId}
          template={previewTemplate}
          locale={uiLocale}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
}
